import React, { useRef, useState } from 'react'
import { Input } from 'iebt-zing-storybook'
import { Option } from 'iebt-zing-storybook/dist/components/Fields/Inputs/Input'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { CustomQuestion } from 'domain/feature/hub/company/meeting-room/settings/ApplicationFormsTab/types'
import { trans } from 'sharedKernel/i18n'
import { FileItem } from 'components/FileItem'
import { showToast } from 'components/toast'
import { DatePickerField } from './DatePickerField'
import {
  CheckboxInput,
  CheckboxOptionRow,
  CheckboxRow,
  CheckboxText,
  FieldError,
  FieldFull,
  FieldGroup,
  FieldLabel,
  OptionLabel,
  QuestionDescription,
  RadioInput,
  RequiredMark,
  UploadFileButton,
} from './style'

export type DynamicAnswerValue = string | string[]

interface DynamicQuestionFieldProps {
  question: CustomQuestion
  value: DynamicAnswerValue
  hasError: boolean
  errorMessage?: string
  onChange: (value: DynamicAnswerValue) => void
}

const getFieldId = (questionId: string): string => `res-form-question-${questionId}`

const UPLOAD_MAX_SIZE_LABEL = '10 MB'
const UPLOAD_MAX_SIZE_BYTES = 10 * 1024 * 1024

export const DynamicQuestionField = ({
  question,
  value,
  hasError,
  errorMessage,
  onChange,
}: DynamicQuestionFieldProps): JSX.Element | null => {
  const [uploadedFile, setUploadedFile] = useState<File | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  if (!question.id) return null

  const fieldId = getFieldId(question.id)
  const stringValue = typeof value === 'string' ? value : ''
  const arrayValue = Array.isArray(value) ? value : []
  const description = question.description?.trim() || undefined

  const choiceLabel = (
    <>
      <FieldLabel htmlFor={fieldId}>
        {question.text}
        {question.required && <RequiredMark>*</RequiredMark>}
      </FieldLabel>
      {description ? <QuestionDescription>{description}</QuestionDescription> : null}
    </>
  )

  const selectOptions: Option[] = [
    { value: '', label: trans('meetingRoom.public.form.selectOption') },
    ...(question.options ?? []).map(option => ({
      value: option.text,
      label: option.text,
    })),
  ]

  switch (question.type) {
    case 'long_text':
      return (
        <FieldFull>
          <Input
            id={fieldId}
            code="textarea"
            showCounter={false}
            isRequired={question.required}
            label={question.text}
            description={description}
            value={stringValue}
            isInvalid={hasError}
            notValidFieldMessage={errorMessage}
            onChange={e => onChange(e.target.value)}
          />
        </FieldFull>
      )

    case 'date':
      return (
        <FieldFull>
          <FieldGroup id={fieldId}>
            {choiceLabel}
            <DatePickerField
              id={`${fieldId}-input`}
              value={stringValue}
              onChange={onChange}
              isInvalid={hasError}
            />
            {hasError && errorMessage ? <FieldError>{errorMessage}</FieldError> : null}
          </FieldGroup>
        </FieldFull>
      )

    case 'number':
      return (
        <FieldFull>
          <Input
            id={fieldId}
            code="number"
            showCounter={false}
            isRequired={question.required}
            label={question.text}
            description={description}
            value={stringValue}
            isInvalid={hasError}
            notValidFieldMessage={errorMessage}
            onChange={e => onChange(e.target.value)}
          />
        </FieldFull>
      )

    case 'link':
      return (
        <FieldFull>
          <Input
            id={fieldId}
            code="url"
            showCounter={false}
            isRequired={question.required}
            label={question.text}
            description={description}
            value={stringValue}
            placeholder="https://"
            isInvalid={hasError}
            notValidFieldMessage={errorMessage}
            onChange={e => onChange(e.target.value)}
          />
        </FieldFull>
      )

    case 'file_upload':
      return (
        <FieldFull id={fieldId}>
          <FieldGroup>
            {choiceLabel}
            {uploadedFile && (
              <FileItem
                file={uploadedFile}
                onRemove={() => {
                  setUploadedFile(null)
                  onChange('')
                }}
              />
            )}
            <input
              ref={fileInputRef}
              type="file"
              style={{ display: 'none' }}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                const file = event.target.files?.[0]
                if (!file) return
                if (file.size > UPLOAD_MAX_SIZE_BYTES) {
                  showToast('warning', `${trans('O arquivo deve ter um tamanho máximo de')} ${UPLOAD_MAX_SIZE_LABEL}`)
                  event.target.value = ''
                  return
                }
                setUploadedFile(file)
                onChange(file.name)
                event.target.value = ''
              }}
            />
            {!uploadedFile && (
              <UploadFileButton type="button" onClick={() => fileInputRef.current?.click()}>
                <FontAwesomeIcon icon={['fas', 'cloud-arrow-up']} style={{ marginRight: '6px' }} />
                {trans('meetingRoom.form.addFile')}
              </UploadFileButton>
            )}
            {hasError && errorMessage ? <FieldError>{errorMessage}</FieldError> : null}
          </FieldGroup>
        </FieldFull>
      )

    case 'dropdown':
      return (
        <FieldFull>
          <Input
            id={fieldId}
            code="select"
            isRequired={question.required}
            label={question.text}
            description={description}
            value={stringValue}
            options={selectOptions}
            isInvalid={hasError}
            notValidFieldMessage={errorMessage}
            onBlur={e => onChange(e.target.value)}
          />
        </FieldFull>
      )

    case 'multiple_choice':
      return (
        <FieldFull>
          <FieldGroup>
            {choiceLabel}
            {(question.options ?? []).map((option, index) => {
              const optionId = `${fieldId}-option-${index}`
              return (
                <CheckboxRow key={option.id || optionId} htmlFor={optionId}>
                  <RadioInput
                    id={optionId}
                    type="radio"
                    name={fieldId}
                    checked={stringValue === option.text}
                    onChange={() => onChange(option.text)}
                  />
                  <CheckboxText>{option.text}</CheckboxText>
                </CheckboxRow>
              )
            })}
            {hasError && errorMessage ? <FieldError>{errorMessage}</FieldError> : null}
          </FieldGroup>
        </FieldFull>
      )

    case 'checkbox':
      return (
        <FieldFull>
          <FieldGroup>
            {choiceLabel}
            {(question.options ?? []).map((option, index) => {
              const optionId = `${fieldId}-option-${index}`
              const isChecked = arrayValue.includes(option.text)
              return (
                <CheckboxOptionRow key={option.id || optionId}>
                  <CheckboxInput
                    id={optionId}
                    type="checkbox"
                    checked={isChecked}
                    onChange={e => {
                      const next = e.target.checked
                        ? [...arrayValue, option.text]
                        : arrayValue.filter(item => item !== option.text)
                      onChange(next)
                    }}
                  />
                  <OptionLabel htmlFor={optionId}>{option.text}</OptionLabel>
                </CheckboxOptionRow>
              )
            })}
            {hasError && errorMessage ? <FieldError>{errorMessage}</FieldError> : null}
          </FieldGroup>
        </FieldFull>
      )

    case 'short_text':
    default:
      return (
        <FieldFull>
          <Input
            id={fieldId}
            showCounter={false}
            isRequired={question.required}
            label={question.text}
            description={description}
            value={stringValue}
            isInvalid={hasError}
            notValidFieldMessage={errorMessage}
            onChange={e => onChange(e.target.value)}
          />
        </FieldFull>
      )
  }
}
